import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOutIcon, MoreHorizontalIcon } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useDocumentAccess } from '../../hooks/useDocumentAccess';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useKeystoneAccess } from '../../hooks/keystone/useKeystoneAccess';
import { useFlaggedNotesCount } from '../../hooks/useFlaggedNotesCount';
import { useFlaggedKeystoneNotesCount } from '../../hooks/keystone/useFlaggedKeystoneNotesCount';
import { NotificationBell } from '../common/NotificationBell';
import { schoolCrestUrl, schoolName } from '../../lib/school-brand';
import { cn } from '../../lib/utils';
import { isItemActive, staffModules, teacherModules, type NavModule } from './navConfig';

export const APP_NAME = 'Learning Diversity';

type Badges = Record<string, { count: number; label: string }>;

const countLabel = (n: number) => (n > 99 ? '99+' : String(n));

function CountBadge({ count, label }: { count: number; label: string }) {
  if (count <= 0) return null;
  return (
    <span
      aria-label={label}
      className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-pill bg-critical px-1.5 text-[11px] font-bold tabular-nums text-white"
    >
      {countLabel(count)}
    </span>
  );
}

/* --------------------------------------------------------------------------
 * Top bar: shared by every variant, including the slim parent shell.
 * ------------------------------------------------------------------------ */

export interface AppTopBarProps {
  /** Shown under the school's name. Defaults to "Learning Diversity". */
  title?: string;
  /** Who is signed in (email). Hidden below 640px. */
  account?: string;
  onSignOut?: () => void;
  /** Show the in-app notification bell (staff and teachers). */
  showNotifications?: boolean;
  /** Extra controls before the account. */
  actions?: ReactNode;
}

export function AppTopBar({ title = APP_NAME, account, onSignOut, showNotifications, actions }: AppTopBarProps) {
  const crest = schoolCrestUrl();
  return (
    <header className="px-topbar">
      <div className="flex min-w-0 items-center gap-3">
        {crest && <img src={crest} alt="" className="px-topbar__crest h-8 w-8 shrink-0 object-contain" />}
        <div className="px-topbar__titles">
          <span className="px-topbar__school">{schoolName()}</span>
          <span className="px-topbar__title">{title}</span>
        </div>
      </div>
      <div className="px-topbar__actions">
        {actions}
        {showNotifications && <NotificationBell />}
        {account && <span className="hidden max-w-[16rem] truncate text-sm text-ink-700 sm:inline">{account}</span>}
        {onSignOut && (
          <button type="button" onClick={onSignOut} className="px-topbar__account">
            <LogOutIcon className="px-icon h-4 w-4" aria-hidden="true" />
            Sign out
          </button>
        )}
      </div>
    </header>
  );
}

/* --------------------------------------------------------------------------
 * Rail (desktop column / phone tab bar) and section tabs.
 * ------------------------------------------------------------------------ */

interface RailProps {
  modules: NavModule[];
  pathname: string;
  badges: Badges;
}

function Rail({ modules, pathname, badges }: RailProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLLIElement>(null);
  const foot = modules.filter((m) => m.foot);

  useEffect(() => setMoreOpen(false), [pathname]);
  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMoreOpen(false);
    const onClick = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [moreOpen]);

  return (
    <nav className="px-rail" aria-label="Main">
      <ul className="px-rail__list">
        {modules.map((m, i) => {
          const Icon = m.icon;
          const active = m.isActive(pathname);
          const badge = badges[m.id];
          const firstFoot = m.foot && !modules[i - 1]?.foot;
          return (
            <li
              key={m.id}
              className="px-rail__entry"
              data-tab={m.foot ? 'secondary' : undefined}
              data-foot={firstFoot ? 'true' : undefined}
            >
              <Link to={m.href} className="px-rail__item" aria-current={active ? 'page' : undefined}>
                <span className="relative">
                  <Icon className="px-icon h-5 w-5" aria-hidden="true" />
                  {badge && badge.count > 0 && (
                    <span className="absolute -right-3 -top-2">
                      <CountBadge count={badge.count} label={badge.label} />
                    </span>
                  )}
                </span>
                {/* Single words ("Notifications") shrink a step rather than break mid-word. */}
                <span className={cn('px-rail__label [overflow-wrap:normal] max-md:text-[0.6875rem]', m.name.length > 11 && 'text-[0.6875rem]')}>{m.name}</span>
              </Link>
            </li>
          );
        })}
        {foot.length > 0 && (
          <li className="px-rail__entry relative" data-tab="more" ref={moreRef}>
            <button
              type="button"
              className="px-rail__item w-full"
              aria-expanded={moreOpen}
              aria-haspopup="true"
              onClick={() => setMoreOpen((o) => !o)}
            >
              <MoreHorizontalIcon className="px-icon h-5 w-5" aria-hidden="true" />
              <span className="px-rail__label">More</span>
            </button>
            {moreOpen && (
              <ul className="px-menu__list" style={{ insetBlockStart: 'auto', insetBlockEnd: 'calc(100% + 0.25rem)', insetInlineStart: 'auto', insetInlineEnd: 0 }}>
                {foot.map((m) => (
                  <li key={m.id}>
                    <Link to={m.href} className="px-menu__item" aria-current={m.isActive(pathname) ? 'page' : undefined}>
                      {m.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        )}
      </ul>
    </nav>
  );
}

function SectionTabs({ module, pathname, badges }: { module: NavModule; pathname: string; badges: Badges }) {
  if (!module.items || module.items.length < 2) return null;
  return (
    <nav aria-label={module.name} className="border-b border-border bg-surface px-4 pt-3 md:px-7">
      <ul className="px-tabs__list px-tabs--links !mb-3 max-md:!flex-nowrap max-md:overflow-x-auto max-md:pb-1">
        {module.items.map((item) => {
          const current = isItemActive(item.href, pathname);
          const badge = badges[item.href];
          return (
            <li key={item.href} className="px-tabs__item shrink-0" data-current={current ? 'true' : undefined}>
              <Link to={item.href} className="px-tabs__tab whitespace-nowrap" aria-current={current ? 'page' : undefined}>
                {item.name}
              </Link>
              {badge && badge.count > 0 && (
                <span className="px-tabs__mark">
                  <CountBadge count={badge.count} label={badge.label} />
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/* --------------------------------------------------------------------------
 * AppShell
 * ------------------------------------------------------------------------ */

export type AppShellVariant = 'staff' | 'teacher' | 'parent';

export interface AppShellProps {
  /** staff: rail + section tabs. teacher: rail with the teacher dashboard. parent: slim, top bar only. */
  variant?: AppShellVariant;
  children: ReactNode;
  /** Parent variant only: the parent session (the staff/teacher variants read useAuth). */
  account?: string;
  onSignOut?: () => void;
  /** Width of the content column. `narrow` (48rem) suits reading/forms; parent defaults to it. */
  width?: 'default' | 'narrow';
}

function MainColumn({ children, width, padded }: { children: ReactNode; width: 'default' | 'narrow'; padded?: boolean }) {
  // The rail shell pads .px-shell__main itself; the slim parent shell does not.
  return (
    <main id="main" tabIndex={-1} className={cn('px-shell__main focus:outline-none', padded && 'px-4 py-6 md:px-7 md:py-8')}>
      <div className={cn('mx-auto w-full', width === 'narrow' ? 'max-w-3xl' : 'max-w-[1400px]')}>{children}</div>
    </main>
  );
}

function SkipLink() {
  return <a className="px-skip-link" href="#main">Skip to content</a>;
}

function RailShell({ variant, modules, railBadges, tabBadges, children, width }: {
  variant: 'staff' | 'teacher';
  modules: NavModule[];
  railBadges: Badges;
  tabBadges: Badges;
  children: ReactNode;
  width: 'default' | 'narrow';
}) {
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();
  const activeModule = modules.find((m) => m.isActive(pathname));
  return (
    <div className="px-shell px-shell--rail" data-variant={variant}>
      <SkipLink />
      <Rail modules={modules} pathname={pathname} badges={railBadges} />
      <div className="px-shell__frame">
        <AppTopBar
          title={variant === 'teacher' ? `${APP_NAME} · Teacher` : APP_NAME}
          account={user?.email ?? undefined}
          onSignOut={signOut}
          showNotifications
        />
        {activeModule && <SectionTabs module={activeModule} pathname={pathname} badges={tabBadges} />}
        <MainColumn width={width}>{children}</MainColumn>
      </div>
    </div>
  );
}

function StaffShell({ children, width }: { children: ReactNode; width: 'default' | 'narrow' }) {
  const { pathname } = useLocation();
  const { canAccessDocuments } = useDocumentAccess();
  const { isAdmin } = useIsAdmin();
  const { isMember: isKeystoneMember, isLeader: isKeystoneLeader } = useKeystoneAccess();
  // Refetch counts on navigation so a badge drops once the user actions it.
  const flaggedCount = useFlaggedNotesCount(pathname);
  const keystoneFlaggedCount = useFlaggedKeystoneNotesCount(pathname);

  const modules = staffModules({ canAccessDocuments, isAdmin, isKeystoneMember, isKeystoneLeader });
  const flagged = { count: flaggedCount, label: `${flaggedCount} unread flagged ${flaggedCount === 1 ? 'note' : 'notes'}` };
  const keystone = {
    count: keystoneFlaggedCount,
    label: `${keystoneFlaggedCount} flagged Keystone ${keystoneFlaggedCount === 1 ? 'note' : 'notes'}`,
  };
  const railBadges: Badges = { keystone };
  // The Flagged page is gated on full access, so its count is too.
  if (canAccessDocuments) railBadges['case-notes'] = flagged;

  return (
    <RailShell variant="staff" modules={modules} railBadges={railBadges} tabBadges={{ '/staff/case-notes/flagged': flagged }} width={width}>
      {children}
    </RailShell>
  );
}

/**
 * AppShell: the one Sprout OS shell for the app (#358). Replaces Navbar,
 * TeacherNavbar, UnifiedNavbar and StaffLayout.
 *
 *   <AppShell variant="staff">…</AppShell>      (StaffLayout is an alias)
 *   <AppShell variant="teacher">…</AppShell>
 *   <AppShell variant="parent" account={email} onSignOut={signOut}>…</AppShell>
 */
export function AppShell({ variant = 'staff', children, account, onSignOut, width }: AppShellProps) {
  if (variant === 'parent') {
    return (
      <div className="px-shell" data-variant="parent">
        <SkipLink />
        <AppTopBar title={`${APP_NAME} documents`} account={account} onSignOut={onSignOut} />
        <MainColumn width={width ?? 'narrow'} padded>{children}</MainColumn>
      </div>
    );
  }
  if (variant === 'teacher') {
    return (
      <RailShell variant="teacher" modules={teacherModules()} railBadges={{}} tabBadges={{}} width={width ?? 'default'}>
        {children}
      </RailShell>
    );
  }
  return <StaffShell width={width ?? 'default'}>{children}</StaffShell>;
}
